<i18n>
  {
    "fr": {
      "connection": "Merci de patienter",
      "connection-message": "Identification en cours.",
      "error": "Erreur",
      "ok": "Succès",
      "ok-message": "Vous êtes authentifié"
    },
    "en": {
      "connection": "Please wait",
      "connection-message": "Identification in process",
      "error": "Error",
      "ok": "Success",
      "ok-message": "You are logged in"
    }
  }
</i18n>

<template>
  <v-card id="login-card">
    <v-card-title>
      <div
        v-if="loading"
      >
        <v-progress-circular
          indeterminate
          color="primary"
          class="mr-4"
        />
        <span>
          {{ $t('connection') }}
        </span>
      </div>
      <div
        v-else-if="!loading"
      >
        <span v-if="error">
          <v-icon color="red">mdi-alert</v-icon>
          {{ $t('error') }}
        </span>
        <span v-else-if="error === undefined">
          {{ $t('ok') }}
        </span>
      </div>
    </v-card-title>
    <v-divider />
    <v-card-text>
      <div v-if="loading">
        {{ $t('connection-message') }}
      </div>
      <div v-else-if="!loading">
        <span v-if="error">
          <v-icon color="red">mdi-alert</v-icon>
          {{ $t('error') }}
        </span>
        <span v-else-if="error === undefined">
          {{ $t('ok-message') }}
        </span>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import moment from 'moment'
import apiUrls from '../../constants/urls'
import loginService from '../../services/loginService'
import tokenStub from '../../stubs/tokenStub'
import globalMixin from '@/mixins/global'

export default {
  name: 'CodeAccept',
  mixins: [globalMixin],
  data: () => ({
    error: undefined
  }),
  computed: {
    ...mapGetters({
      token: 'general/getToken',
      loading: 'general/getServiceLoadingState'
    })
  },
  created () {
    if (process.env.NODE_ENV === 'development') {
      this.changeServiceLoadingState(true)
      const validToken = this.tokenValidator(tokenStub.access_token)
      this.changeToken(validToken)
      this.changeServiceLoadingState(false)
    } else {
      this.init()
    }
  },
  methods: {
    ...mapActions({
      changeServiceLoadingState: 'general/changeServiceLoadingState',
      changeToken: 'general/changeToken',
      changeLoggedState: 'general/changeLoggedState',
      changeCustomer: 'general/changeCustomer'
    }),

    /**
     * The created lifecycle hook use this method to await for the different services call
     * @return {undefined}
     */
    async init () {
      try {
        await this.getToken()
        await this.getUserInfo()
        this.changeLoggedState(true)
        this.routerPushHome()
      } catch (e) {
        console.error(e)
        this.routerPushHome()
      }
    },

    /**
     * This method call loginService with the code url parameter and parse the response
     * @return {undefined}
     */
    async getToken () {
      const response = await loginService.getToken(this.$route.query.code)

      // Verify token validity
      if (response.data.access_token) {
        if (this.tokenValidator(response.data.access_token)) {
          this.$store.dispatch('general/changeToken', response.data.access_token)
          this.$store.dispatch('general/changeSessionExpirationTime', response.data.expires_in)
        } else {
          throw Error(`InvalidToken: ${response.data.access_token}`)
        }
      } else if (response.data.error) {
        throw Error(response.data.error_description)
      } else {
        throw Error('invalid service response')
      }
    },

    /**
     * This method decodes a base64 string and then validate the content
     * @param token
     * @return {string}
     */
    tokenValidator (token) {
      const splitBase64 = token.split('.')
      const tokenHeader = JSON.parse(window.atob(splitBase64[0]))
      const tokenClaims = JSON.parse(window.atob(splitBase64[1]))

      // The Issuer Identifier MUST exactly match the value of the iss (issuer) Claim.
      const issuerIdentifier = tokenClaims.iss === apiUrls.sfi.issuerIdentifier
      // The Client MUST validate that the aud (audience) Claim contains its client_id value registered at the Issuer identified by the iss (issuer) Claim as an audience.
      const audience = tokenClaims.aud === apiUrls.sfi.clientId
      // The alg value SHOULD be the default of RS256.
      const algorithm = tokenHeader.alg === 'RS256'
      // Current time MUST be before the time represented by the exp Claim.
      const expiration = moment().isBefore(moment(tokenClaims.exp, 'X'))
      return issuerIdentifier && algorithm && audience && expiration
    },

    /**
     * This method call loginService to retrieve user info and then parse the response
     * @return {undefined}
     */
    async getUserInfo () {
      const response = await loginService.getUser(this.token)
      const customer = this.customerParser(response.data)
      customer.family = this.familyParser(customer, response.data)
      this.changeCustomer(customer)
    },

    /**
     *
     * @param {Object} response The response from an ajax request
     * @return {Object} The customer object parsed with the correct keys
     */
    customerParser (response) {
      const birthday = response.birth_date ? moment(response.birth_date, 'DD/MM/YYYY').format('YYYY-MM-DD') : ''
      return {
        connexion_token: response.connexion_token,
        registration_number: response.registration_number,
        first_name: response.first_name,
        last_name: response.last_name,
        usage_first_name: response.usage_first_name,
        usage_last_name: response.usage_last_name,
        birthday: birthday,
        street_1: response.address_street_address,
        street_2: '',
        zip_code: response.address_postal_code,
        city: response.address_locality,
        country_code: '',
        country_name: '',
        phone_mobile: response.phone_number,
        phone_personnal: '',
        phone_business: '',
        email: response.email,
        civility: response.gender === 'Masculin' ? 'mr' : 'mrs'
      }
    },

    /**
     *
     * @param {Object} parsedCustomer The parsed customer object
     * @param {Object} response The response from an ajax request
     * @return {Array} The family Array with all his members
     */
    familyParser (parsedCustomer, response) {
      const family = []
      const mainTraveller = {
        first_name: parsedCustomer.first_name,
        last_name: parsedCustomer.last_name,
        usage_first_name: parsedCustomer.usage_first_name,
        usage_last_name: parsedCustomer.usage_last_name,
        birthday: parsedCustomer.birthday,
        registration_number: parsedCustomer.registration_number,
        civility: parsedCustomer.civility
      }
      family.push(mainTraveller)

      if (response.family) {
        let member
        for (member of response.family) {
          const traveller = {
            registration_number: member.registration_number,
            first_name: member.first_name,
            last_name: member.last_name,
            usage_first_name: member.usage_first_name,
            usage_last_name: member.usage_last_name,
            birthday: moment(member.birth_date, 'DD/MM/YYYY').format('YYYY-MM-DD'),
            civility: member.gender === 'Masculin' ? 'mr' : 'mrs'
          }
          family.push(traveller)
        }
      }
      return family
    }

  }
}
</script>

<style scoped>
  #login-card {
    margin-top: 20vh;
    margin-left: auto;
    margin-right: auto;
    width: 50%;
  }

</style>
